/**
 * AVA Assistant constants: the one place mode, view, storage and layering names live.
 * No Vue or path-alias imports, so domain modules and node tests can share them.
 */

export const ASSISTANT_MODE = Object.freeze({ DRAWER: 'drawer', PANEL: 'panel', PAGE: 'page' });

export const ASSISTANT_VIEW = Object.freeze({
  CHAT: 'chat',
  HISTORY: 'history',
  FILES: 'files',
  PREVIEW: 'preview',
  SETTINGS: 'settings',
  /** "What AVA is using": the conversation context (Khalid, 2026-10-22). */
  CONTEXT: 'context',
});

/** localStorage key for the remembered mode, panel width and drawer rect (per browser). */
export const ASSISTANT_STORAGE_KEY = 'ava-assistant-geometry';
/** Per-person settings in this browser; the user id is appended (`ava-assistant-prefs:<userId>`). */
export const ASSISTANT_PREFS_STORAGE_KEY = 'ava-assistant-prefs';
/** CSS variable the message list and composer scale their text by (Settings → Text size). */
export const ASSISTANT_TEXT_SCALE_VAR = '--asx-text-scale';

/**
 * Above app content, menus and app dialogs with their dim overlay (Vuetify 201/202+): the chat is a side panel that stays
 * usable while a screen it opened (e.g. Register Import) is up (Khalid, 2026-10-16). The assistant has no Vuetify
 * overlays of its own; platform/dialogCoexistence.js keeps app dialogs from closing or taking focus back on chat clicks.
 */
export const ASSISTANT_Z_INDEX = 300;

/** CSS custom property the layouts read to make room for the side panel. */
export const ASSISTANT_INSET_VAR = '--ava-assistant-inset';

/** DOM id of the assistant's own Vue root, appended to <body>. */
export const ASSISTANT_ROOT_ID = 'ava-assistant-root';

/** The assistant surface (AvaAssistant's root): "is focus inside the assistant?" checks against it. */
export const ASSISTANT_SURFACE_SELECTOR = '.ava-assistant';

/** Viewport width at and below which the assistant is full-screen and not movable. */
export const ASSISTANT_COMPACT_MAX_WIDTH = 760;

/** Page mode collapses the history column when a file opens below this viewport width. */
export const ASSISTANT_PAGE_SPLIT_MIN_WIDTH = 1280;

/**
 * Empty-state copy per page-context kind (domain/pageContext.js PAGE_CONTEXT_KIND).
 * Suggestions are sent as the user's question when clicked. The facility ones were each run live on Merchant (2026-10-22):
 * they name the scope ("on all boards") so AVA answers at once instead of asking which board.
 */
export const LANDING_COPY = Object.freeze({
  facility: {
    headline: (label) => `What should we look at in ${label}?`,
    suggestions: [
      { title: 'Review AI findings', prompt: 'Which AI findings are waiting for my review?' },
      { title: 'Open work orders', prompt: 'Show me the open work orders on all boards' },
      { title: 'Chart the tasks', prompt: 'Chart the tasks on all boards by due status' },
      { title: 'Find overdue work', prompt: 'Which tasks are overdue on all boards?' },
    ],
  },
  facilities: {
    headline: () => 'What can I help you with in AVA?',
    suggestions: [
      { title: 'Compare facilities', prompt: 'Compare open anomalies across all facilities' },
      { title: 'Find overdue work', prompt: 'Which facilities have overdue inspections?' },
      { title: 'Build a dashboard', prompt: 'Build a dashboard of integrity status across facilities' },
      { title: 'Summarise the week', prompt: 'Summarise what changed across AVA this week' },
    ],
  },
  administration: {
    headline: () => 'What do you need to manage?',
    suggestions: [
      { title: 'Check access', prompt: 'Who can edit dashboards in Hamriyah Plant?' },
      { title: 'Add people', prompt: 'Invite three inspectors to Serica' },
      { title: 'Review activity', prompt: 'Show changes made by my team this week' },
      { title: 'Find a user', prompt: 'Which users have not signed in for 90 days?' },
    ],
  },
  app: {
    headline: () => 'What can I help you with in AVA?',
    suggestions: [
      { title: 'Build a dashboard', prompt: 'Build a dashboard of open anomalies by unit' },
      { title: 'Find overdue work', prompt: 'Which inspections are overdue?' },
      { title: 'Summarise for a meeting', prompt: 'Summarise high-severity anomalies for the integrity meeting' },
      { title: 'Ask anything', prompt: 'What can you do in AVA?' },
    ],
  },
});

/** Icon per artifact kind, shared by the file card, the files list and the tabs. */
export const ARTIFACT_ICON = Object.freeze({ dashboard: 'squares-2x2', markdown: 'document-text', pdf: 'document-text', chart: 'chart-bar' });
export const DEFAULT_ARTIFACT_ICON = 'document-text';
